import {generatePath} from 'react-router';

import {appRoute} from '../../../../app-route';
import {CalendarPostsResponseType, CalendarPostStatusEnum} from '../../../../service/posts/posts-types';
import {
    CalendarPublicationEntryType,
    CalendarPublicationsResponseType,
    CalendarPublicationTypeEnum,
} from '../../../../service/publications/publications-calendar-type';
import {objectToUrlParameters} from '../../../../util/url';
import {PostsTabEnum} from '../posts-const';
import {PostsFilterStateUrlQueryNameEnum} from '../posts-filter/posts-filter-type';

import {MergedCalendarType, PostInsideDayCardType, PostInsideMonthCardType} from './posts-calendar-type';

const TAB_BY_PUBLICATION_TYPE: Record<CalendarPublicationTypeEnum, PostsTabEnum | null> = {
    [CalendarPublicationTypeEnum.post]: null,
    [CalendarPublicationTypeEnum.promotion]: PostsTabEnum.Promotions,
    [CalendarPublicationTypeEnum.event]: PostsTabEnum.Events,
};

// The posts endpoint predates the shared entry shape, so its entries are normalized here rather
// than on the backend, which stays untouched.
function normalizePostEntry(post: {id: number; name: string}): CalendarPublicationEntryType {
    return {
        id: post.id,
        name: post.name,
        type: CalendarPublicationTypeEnum.post,
        moderation: null,
    };
}

export function mergeCalendarSources(
    calendarPosts: CalendarPostsResponseType | null,
    calendarPublications: CalendarPublicationsResponseType | null
): MergedCalendarType | null {
    if (!calendarPosts && !calendarPublications) {
        return null;
    }

    const merged: MergedCalendarType = {};

    Object.entries(calendarPosts ?? {}).forEach(([index, byStatus]) => {
        const bucket: NonNullable<MergedCalendarType[number]> = {};

        Object.entries(byStatus ?? {}).forEach(([status, entries]) => {
            bucket[status as CalendarPostStatusEnum] = (entries ?? []).map(normalizePostEntry);
        });

        merged[Number(index)] = bucket;
    });

    Object.entries(calendarPublications ?? {}).forEach(([index, byStatus]) => {
        const bucket = merged[Number(index)] ?? {};

        Object.entries(byStatus ?? {}).forEach(([status, entries]) => {
            const key = status as CalendarPostStatusEnum;

            bucket[key] = [...(bucket[key] ?? []), ...(entries ?? [])];
        });

        merged[Number(index)] = bucket;
    });

    return merged;
}

export function getCalendarEntryLink(entry: PostInsideDayCardType): string {
    const tab = TAB_BY_PUBLICATION_TYPE[entry.type];

    if (!tab) {
        return generatePath(appRoute.postsManagementViewPost.path, {postId: entry.id});
    }

    // Promotions and Events have no detail route; the list tab is the closest destination.
    return `${appRoute.postsManagement.path}?${objectToUrlParameters({
        [PostsFilterStateUrlQueryNameEnum.tab]: tab,
    })}`;
}

export function extractPostsByIndexForMonthView(
    calendarPosts: MergedCalendarType | null,
    index: number
): Array<PostInsideDayCardType> | null {
    if (!calendarPosts) {
        return null;
    }

    const calendarPostsByStatuses = calendarPosts[index];

    if (!calendarPostsByStatuses) {
        return null;
    }

    return Object.keys(calendarPostsByStatuses).reduce<Array<PostInsideDayCardType>>((accumulator, status) => {
        const posts =
            calendarPostsByStatuses[status as keyof typeof calendarPostsByStatuses]?.map((post) => ({
                ...post,
                status: status as keyof typeof calendarPostsByStatuses,
            })) ?? [];

        return [...accumulator, ...posts];
    }, []);
}

export function extractPostsByIndexForYearView(
    calendarPosts: MergedCalendarType | null,
    index: number
): Array<PostInsideMonthCardType> | null {
    if (!calendarPosts) {
        return null;
    }

    const calendarPostsByStatuses = calendarPosts[index];

    if (!calendarPostsByStatuses) {
        return null;
    }

    return Object.keys(calendarPostsByStatuses)
        .filter((status) => (calendarPostsByStatuses[status as keyof typeof calendarPostsByStatuses]?.length ?? 0) > 0)
        .map<PostInsideMonthCardType>((status) => ({
            status: status as CalendarPostStatusEnum,
            count: calendarPostsByStatuses[status as keyof typeof calendarPostsByStatuses]?.length ?? 0,
        }));
}
